/**
 * WorkflowRecordOpener - Abre a ficha oficial do registro de origem do Workflow
 * -----------------------------------------------------------------------------
 * Catálogo único `root_entity_type → ficha oficial`, usado pelas páginas de
 * Pendências, Execuções e Erros do processo (atalho "Abrir registro").
 *
 * Reaproveita as mesmas fichas do restante do sistema (Negócio, Tarefa,
 * Cobrança, Conversa, Proposta, Contrato, Vistoria e Veículo) — nenhuma ficha
 * paralela é criada. Para incluir um novo tipo, basta acrescentar uma entrada
 * em `RECORD_KIND`. Apenas UMA ficha fica aberta por vez (evita sobreposição de
 * modais de negócio, regra já adotada no projeto).
 *
 * @version 1.1.0
 * RELEASE: 2026-10-06 v1.1.0 - Fichas com abas (Negócio, Tarefa e Veículo) abrem
 *          direto na aba "Workflow" do registro, refletindo o contexto de origem.
 * RELEASE: 2026-10-06 v1.0.0 - Criação inicial (submenus do Workflow).
 */

import { useCallback, useState, type ReactNode } from 'react';
import { DealDetailsModalPremium } from '@/components/crm/deals/modal/DealDetailsModalPremium';
import { TaskDetailsModal } from '@/components/tasks/modal/TaskDetailsModal';
import { ChargeDetailsModal } from '@/components/financial/ChargeDetailsModal';
import { ChatConversationModal } from '@/components/tenant/kanban/ChatConversationModal';
import { ProposalDetailsModal } from '@/components/crm/deals/modal/tabs/proposals/ProposalDetailsModal';
import { ContractDetailSheet } from '@/components/contracts/ContractDetailSheet';
import { InspectionDialog } from '@/components/checklist/inspection/InspectionDialog';
import { VehicleDetailsModalPremium } from '@/components/vehicles/modal/VehicleDetailsModalPremium';
import type { FinanceiroCobranca } from '@/hooks/useFinanceiroCobrancas';

/** Tipo de ficha a abrir para cada entidade-raiz do Workflow */
type RecordKind = 'deal' | 'task' | 'charge' | 'conversation' | 'proposal' | 'contract' | 'inspection' | 'vehicle';

/** Catálogo central: entidade-raiz → ficha oficial */
const RECORD_KIND: Record<string, RecordKind> = {
  crm_deal: 'deal',
  task: 'task',
  retention: 'task', // Retenção é uma tarefa com task_type próprio
  legal: 'task',     // Jurídico idem
  billing: 'charge',
  conversation: 'conversation',
  proposal: 'proposal',
  contract: 'contract',
  inspection: 'inspection',
  vehicle: 'vehicle',
};

/** Rótulos amigáveis das entidades (exibidos nas listagens) */
export const WORKFLOW_ENTITY_LABEL: Record<string, string> = {
  crm_deal: 'Negócio',
  task: 'Tarefa',
  retention: 'Retenção',
  legal: 'Jurídico',
  billing: 'Cobrança',
  conversation: 'Conversa',
  proposal: 'Proposta',
  contract: 'Contrato',
  inspection: 'Vistoria',
  vehicle: 'Veículo',
  service_order: 'Ordem de serviço',
  integration: 'Integração',
  workflow: 'Workflow',
};

/** Indica se existe ficha para o tipo informado (habilita o botão) */
export function canOpenWorkflowRecord(type: string | null | undefined): boolean {
  return !!type && type in RECORD_KIND;
}

/**
 * Hook que devolve a função `openRecord` e o elemento com as fichas montadas.
 * O elemento deve ser renderizado uma única vez na página.
 */
export function useWorkflowRecordOpener(): {
  openRecord: (type: string | null | undefined, id: string | null | undefined) => void;
  element: ReactNode;
} {
  const [atual, setAtual] = useState<{ kind: RecordKind; id: string } | null>(null);

  const openRecord = useCallback((type: string | null | undefined, id: string | null | undefined) => {
    if (!type || !id) return;
    const kind = RECORD_KIND[type];
    if (!kind) return;
    setAtual({ kind, id }); // substitui a ficha anterior (nunca empilha)
  }, []);

  const fechar = () => setAtual(null);
  const idDe = (k: RecordKind) => (atual?.kind === k ? atual.id : null);

  const element = (
    <>
      {/* v1.1.0: fichas com abas abrem direto na aba "Workflow" do registro */}
      <DealDetailsModalPremium dealId={idDe('deal')} open={!!idDe('deal')} onOpenChange={(o) => !o && fechar()} initialTab="workflow" />
      <TaskDetailsModal taskId={idDe('task')} open={!!idDe('task')} onOpenChange={(o) => !o && fechar()} initialTab="workflow" />
      <ChargeDetailsModal
        cobranca={idDe('charge') ? ({ id: idDe('charge') } as FinanceiroCobranca) : null}
        open={!!idDe('charge')}
        onOpenChange={(o) => !o && fechar()}
      />
      <ChatConversationModal
        conversationId={idDe('conversation')}
        open={!!idDe('conversation')}
        onOpenChange={(o) => !o && fechar()}
      />
      <ProposalDetailsModal proposalId={idDe('proposal')} onClose={fechar} />
      {idDe('contract') && <ContractDetailSheet contractId={idDe('contract')} onClose={fechar} />}
      <InspectionDialog instanceId={idDe('inspection')} onOpenChange={(o) => !o && fechar()} />
      <VehicleDetailsModalPremium vehicleId={idDe('vehicle')} open={!!idDe('vehicle')} onOpenChange={(o) => !o && fechar()} initialTab="workflow" />
    </>
  );

  return { openRecord, element };
}
